import axios, { AxiosError } from "axios";

const TOKEN_KEY = "argos.access";
const REFRESH_KEY = "argos.refresh";

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresInSeconds: number;
}

export const tokenStore = {
  read(): AuthTokens | null {
    const access = localStorage.getItem(TOKEN_KEY);
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!access || !refresh) return null;
    return { accessToken: access, refreshToken: refresh, expiresInSeconds: 0 };
  },
  write(tokens: AuthTokens) {
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const api = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const tokens = tokenStore.read();
  if (tokens) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  return config;
});

let refreshPromise: Promise<AuthTokens> | null = null;

async function performRefresh(): Promise<AuthTokens> {
  const tokens = tokenStore.read();
  if (!tokens) throw new Error("no tokens");
  const res = await axios.post<AuthTokens>(
    "/api/auth/refresh",
    { refreshToken: tokens.refreshToken },
    { headers: { "Content-Type": "application/json" } }
  );
  tokenStore.write(res.data);
  return res.data;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosError["config"] & { _retry?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retry && original.url !== "/auth/refresh") {
      original._retry = true;
      try {
        if (!refreshPromise) refreshPromise = performRefresh();
        const tokens = await refreshPromise;
        refreshPromise = null;
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>)["Authorization"] = `Bearer ${tokens.accessToken}`;
        return api.request(original);
      } catch (refreshError) {
        refreshPromise = null;
        tokenStore.clear();
        if (typeof window !== "undefined") {
          window.location.assign("/login?session=expired");
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);
